import React, { Fragment, memo, useEffect, useMemo } from 'react'
import tw from 'twin.macro'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet } from 'react-router-dom'
import { KillswitchBanner } from 'src/components/payments-ops/KillswitchBanner'
import { GSIcon } from 'src/components/shared/GSIcon'
import { AdminFetchCustomers, AdminFetchKillswitchFlags } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { useInternalPermissions } from 'src/hooks/useInternalPermissions'
import { getCustomers } from 'src/selectors/customer'
import { SideNav } from 'src/components/layout/SideNav'
import { SideNavGroup } from 'src/components/layout/SideNavGroup'
import { SideNavItem } from 'src/components/layout/SideNavItem'
import { filterSectionsByPermission } from 'src/utils/internalAccess/filterByPermission'
import { PAYMENTS_OPS_SECTIONS } from './paymentsOpsNav'

/** Payments Ops — a standalone app with its own rail, laid out like `AdminDashboardLayout`. */
const PaymentsOpsLayoutComponent = () => {
  const dispatch = useDispatch()
  const { isAuthenticated } = useAuthSession()
  const customers = useSelector(getCustomers)
  const hasCustomers = customers.length > 0
  const permissions = useInternalPermissions()
  const canViewPartners = useHasPermission(InternalPermission.PartnersView)
  const canViewFlags = useHasPermission(InternalPermission.FeatureFlagsView)

  // Rail entries mirror the route guards in AppRoutes: a hidden entry is also a "No access" route.
  const visibleSections = useMemo(() => {
    return filterSectionsByPermission(PAYMENTS_OPS_SECTIONS, permissions)
  }, [permissions])

  // Plain `dispatch`, not the toast variant: partner names are enrichment and the banner is
  // advisory. Each GET is skipped without its permission (spec §6) instead of firing a 403:
  // partner names then render as ids, partner pickers are hidden, the banner stays hidden and
  // killswitch-dependent actions fall back to `canRelyOnKillswitchStatus`.
  useEffect(() => {
    if (!isAuthenticated || !canViewPartners || hasCustomers) {
      return
    }

    dispatch(AdminFetchCustomers.request())
  }, [canViewPartners, dispatch, hasCustomers, isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || !canViewFlags) {
      return
    }

    dispatch(AdminFetchKillswitchFlags.request())
  }, [canViewFlags, dispatch, isAuthenticated])

  return (
    <Fragment>
      <SideNav>
        {visibleSections.map((section) => {
          return (
            <SideNavGroup key={section.label} label={section.label}>
              {section.items.map((item) => {
                return (
                  <SideNavItem
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    icon={<GSIcon iconName={item.iconName} size={20} />}
                    dataTestId={`payments-ops-nav-${item.slug}`}
                  />
                )
              })}
            </SideNavGroup>
          )
        })}
      </SideNav>
      <div css={tw`flex min-w-0 flex-1 flex-col`}>
        {canViewFlags ? <KillswitchBanner /> : null}
        <Outlet />
      </div>
    </Fragment>
  )
}

export const PaymentsOpsLayout = memo(PaymentsOpsLayoutComponent)
